import { Injectable, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import moment from 'moment';
import {
  DEFAULT_LANGUAGE,
  LANGUAGES,
  LANGUAGE_STORAGE_KEY,
  Language,
  normalizeLanguage,
} from './i18n.config';
import { currentLangOf } from './translation-manager.service';

/** Angular-локаль, яку реєструє `AppModule`, для відповідної мови застосунку. */
const MOMENT_LOCALE: Record<Language, string> = {
  ua: 'uk',
  en: 'en',
};

/**
 * Єдина точка правди про активну мову інтерфейсу.
 *
 * Перемикання мови НЕ перезавантажує сторінку: `TranslateService.use()`
 * підмінює словник, `TranslationManagerService` дотягує вже задіяні модулі,
 * а `translate`-пайпи перемальовуються по `onTranslationChange`.
 */
@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly translate = inject(TranslateService);

  private readonly language$$ = new BehaviorSubject<Language>(readStoredLanguage());

  /** Сигнал для шаблонів / computed-ів. */
  public readonly language = signal<Language>(this.language$$.value);

  public readonly languageChanges: Observable<Language> = this.language$$.asObservable();

  public readonly available: readonly Language[] = LANGUAGES;

  public get current(): Language {
    return this.language$$.value;
  }

  /**
   * Перемикає мову «на льоту».
   * Повертає стрім, що завершується, коли новий базовий словник застосовано.
   */
  public setLanguage(language: string | null | undefined): Observable<Language> {
    const next = normalizeLanguage(language);

    if (next === currentLangOf(this.translate) && next === this.current) {
      return of(next);
    }

    this.persist(next);

    return this.translate.use(next).pipe(
      catchError(() => of(null)),
      tap(() => this.applySideEffects(next)),
      map(() => next),
    );
  }

  /** Синхронізує локаль без HTTP — використовується на старті, коли словник уже є. */
  public syncFromStorage(): Language {
    const language = readStoredLanguage();
    this.persist(language);
    this.applySideEffects(language);
    return language;
  }

  private persist(language: Language): void {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    if (this.language$$.value !== language) {
      this.language$$.next(language);
    }
    this.language.set(language);
  }

  private applySideEffects(language: Language): void {
    moment.locale(MOMENT_LOCALE[language]);
    document.documentElement.lang = language;
  }
}

/** Мова зі сховища; фолбек — мова браузера, далі — дефолт. */
export function readStoredLanguage(): Language {
  const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  if (stored) {
    return normalizeLanguage(stored);
  }

  const browser = typeof navigator !== 'undefined' ? navigator.language : null;
  const detected = normalizeLanguage(browser);
  return detected ?? DEFAULT_LANGUAGE;
}
